<!-- PAGE HEADER -->
<div class="mx-4 mb-5 w-fit max-w-[calc(100%-2rem)] rounded-2xl border border-gray-300 bg-gradient-to-r from-gray-50 to-white px-4 py-3 sm:mx-5 sm:max-w-[calc(100%-2.5rem)] sm:px-5">
  <div class="border-l-4 border-gray-800 pl-4">
    <p class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
      Manage Application Section
    </p>

    <h2 class="mt-1 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
      Platforms
    </h2>
  </div>
</div>

<app-manage-application-nav></app-manage-application-nav>

<!-- LOADING -->
<ng-template #loading>
  <div class="fixed inset-0 z-50 grid place-items-center bg-white/60 px-4 backdrop-blur-sm">
    <div class="grid w-full max-w-xs justify-items-center gap-4 rounded-2xl border border-gray-300 bg-white px-6 py-6 sm:px-8">
      <div class="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-gray-700"></div>

      <p class="text-center text-sm font-medium text-gray-500">
        Loading platforms...
      </p>
    </div>
  </div>
</ng-template>

<ng-container *ngIf="hasLoaded; else loading">
  <main class="mt-5 px-4 sm:px-5">
    <!-- TOOLBAR -->
    <section class="mb-5">
      <div class="grid grid-cols-12 items-center gap-4 rounded-2xl border border-gray-300 bg-white/95 p-3 backdrop-blur sm:p-4">
        <!-- ADD BUTTON -->
        <div class="col-span-12 md:col-span-3">
          <button
            type="button"
            class="primary-btn h-[50px] w-full rounded-xl px-5 text-sm font-semibold transition md:w-auto"
            (click)="addNewPlatform()"
          >
            Add Platform
          </button>
        </div>

        <!-- SEARCH + PAGINATOR -->
        <div class="col-span-12 md:col-span-9">
          <div class="grid grid-cols-12 items-center gap-3">
            <!-- SEARCH -->
            <div class="col-span-12 xl:col-span-7">
              <div class="grid h-[50px] w-full grid-cols-12 items-center rounded-xl border border-gray-300 bg-gray-50 px-3 transition focus-within:border-gray-300 focus-within:bg-white sm:max-w-md xl:ml-auto">
                <mat-icon class="col-span-1 text-gray-400">
                  search
                </mat-icon>

                <input
                  #searchInput
                  type="text"
                  placeholder="Search..."
                  (keyup.enter)="onSearchSubmit(searchInput.value)"
                  (input)="searchValue = searchInput.value"
                  [(ngModel)]="searchValue"
                  class="col-span-10 min-w-0 bg-transparent px-2 text-sm text-gray-700 outline-none placeholder-gray-400"
                />

                <button
                  type="button"
                  class="col-span-1 grid place-items-center rounded-full p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                  [class.opacity-0]="!searchValue"
                  [class.pointer-events-none]="!searchValue"
                  (click)="clearSearch(); searchInput.value=''; searchValue=''"
                >
                  <mat-icon class="cursor-pointer text-lg">
                    close
                  </mat-icon>
                </button>
              </div>
            </div>

            <!-- PAGINATOR -->
            <div class="col-span-12 min-w-0 overflow-x-auto xl:col-span-5">
              <div class="grid justify-items-start sm:justify-items-center xl:justify-items-end">
                <div class="w-fit min-w-max overflow-hidden rounded-xl bg-white">
                  <mat-paginator
                    [length]="totalItems"
                    [pageSize]="pageSize"
                    [pageIndex]="currentPage - 1"
                    [pageSizeOptions]="[pageSize]"
                    showFirstLastButtons
                    (page)="onPageChange($event)"
                  >
                  </mat-paginator>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PLATFORMS TABLE -->
    <section class="grid grid-cols-1 gap-4">
      <div class="col-span-1 overflow-hidden rounded-2xl border border-gray-300 bg-white">
        <div class="w-full overflow-x-auto">
          <mat-table
            [dataSource]="dataSource"
            matSort
            class="!inline-table !w-full min-w-[980px] table-fixed table-alternate text-sm"
            (matSortChange)="onSortChange($event)"
          >
            <!-- ID COLUMN -->
            <ng-container matColumnDef="id">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="w-[120px] whitespace-nowrap bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500"
              >
                ID
              </th>

              <td
                mat-cell
                *matCellDef="let element"
                class="w-[120px] whitespace-nowrap px-5 py-5 text-sm font-medium text-gray-800"
              >
                #{{ element.id }}
              </td>
            </ng-container>

            <!-- PLATFORM COLUMN -->
            <ng-container matColumnDef="platform">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="w-[120px] whitespace-nowrap bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500"
              >
                Platform
              </th>

              <td
                mat-cell
                *matCellDef="let element"
                class="w-[120px] whitespace-nowrap px-5 py-5"
              >
                <span
                  class="inline-flex w-[120px] items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold"
                  [ngClass]="{
                    'bg-blue-100 text-blue-800': element.platform === 'Facebook',
                    'bg-purple-100 text-purple-800': element.platform === 'Instagram',
                    'bg-black text-white': element.platform === 'TikTok',
                    'bg-red-100 text-red-800': element.platform === 'YouTube',
                    'bg-gray-200 text-gray-800': !element.platform || element.platform === '---'
                  }"
                >
                  {{ element.platform || '---' }}
                </span>
              </td>
            </ng-container>

            <!-- DOMAIN COLUMN -->
            <ng-container matColumnDef="domain">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="w-[200px] whitespace-nowrap bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500"
              >
                Domain
              </th>

              <td
                mat-cell
                *matCellDef="let element"
                class="w-[200px] px-5 py-5"
              >
                <div class="max-w-[320px] overflow-hidden">
                  <a
                    [href]="'https://' + element.domain"
                    target="_blank"
                    class="inline-grid max-w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-2 text-sm font-medium text-gray-800 transition hover:text-blue-600 hover:underline"
                  >
                    <mat-icon class="!text-[18px]">
                      open_in_new
                    </mat-icon>

                    <span class="truncate">
                      {{ element.domain || '---' }}
                    </span>
                  </a>
                </div>
              </td>
            </ng-container>

            

            <!-- STATUS COLUMN -->
            <ng-container matColumnDef="status">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="w-[120px] whitespace-nowrap bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500"
              >
                Status
              </th>

              <td
                mat-cell
                *matCellDef="let element"
                class="w-[120px] whitespace-nowrap px-5 py-5"
              >
                <span
                  class="inline-flex w-[100px] items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold"
                  [ngClass]="{
                    'bg-green-100 text-green-800': element.is_active === 1,
                    'bg-gray-100 text-gray-800': element.is_active === 0
                  }"
                >
                  {{ element.is_active === 1 ? 'Active' : 'Inactive' }}
                </span>
              </td>
            </ng-container>

            <!-- CREATED AT COLUMN -->
            <ng-container matColumnDef="created_at">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="w-[170px] whitespace-nowrap bg-gray-50 px-5 py-4 text-left text-xs font-semibold uppercase tracking-[0.14em] text-gray-500"
              >
                Created At
              </th>

              <td
                mat-cell
                *matCellDef="let element"
                class="w-[170px] whitespace-nowrap px-5 py-5 text-sm text-gray-600"
              >
                {{ element.created_at | date:'dd/MM/yyyy HH:mm' }}
              </td>
            </ng-container>

            <!-- TOTAL POSTS COLUMN -->
            <ng-container matColumnDef="total_published_posts">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="w-[150px] whitespace-nowrap bg-gray-50 px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-gray-500"
              >
                Total Posts
              </th>

              <td
                mat-cell
                *matCellDef="let element"
                class="w-[150px] whitespace-nowrap px-5 py-5 text-center text-sm font-semibold text-gray-900"
              >
                {{ element.total_published_posts || 0 }}
              </td>
            </ng-container>

            <!-- ACTIONS COLUMN -->
            <ng-container matColumnDef="actions">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="w-[150px] whitespace-nowrap bg-gray-50 px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-gray-500"
              >
                Actions
              </th>

              <td
                mat-cell
                *matCellDef="let element"
                class="w-[150px] whitespace-nowrap px-5 py-5"
              >
                <div class="grid w-full grid-cols-2 items-center justify-center gap-1">
                  <button
                    mat-icon-button
                    type="button"
                    class="cursor-pointer text-gray-600 transition-colors hover:text-blue-600"
                    (click)="editPlatform(element)"
                  >
                    <mat-icon>edit</mat-icon>
                  </button>

                  <button
                    mat-icon-button
                    type="button"
                    class="cursor-pointer text-gray-600 transition-colors hover:text-red-600"
                    (click)="openDeleteConfirm(element)"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
              </td>
            </ng-container>

            <!-- HEADER AND ROW DEFINITIONS -->
            <tr
              mat-header-row
              *matHeaderRowDef="displayedColumns"
              class="border-b border-gray-300 bg-gray-50"
            ></tr>

            <tr
              mat-row
              *matRowDef="let row; columns: displayedColumns;"
              class="border-b border-gray-100 bg-white transition-colors even:bg-gray-50/40 hover:bg-gray-100"
            ></tr>
          </mat-table>
        </div>
      </div>

      <!-- BOTTOM PAGINATOR -->
      <div
        *ngIf="dataSource.data.length > 0"
        class="col-span-1 grid grid-cols-12 items-center rounded-2xl bg-white px-3 py-2"
      >
        <div class="col-span-12 min-w-0 overflow-x-auto">
          <div class="grid justify-items-start sm:justify-items-end">
            <div class="w-fit min-w-max overflow-hidden rounded-xl bg-white">
              <mat-paginator
                [length]="totalItems"
                [pageSize]="pageSize"
                [pageIndex]="currentPage - 1"
                [pageSizeOptions]="[pageSize]"
                showFirstLastButtons
                (page)="onPageChange($event)"
              >
              </mat-paginator>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</ng-container>

<!-- TEMPLATES -->
<ng-template #platformFormTmpl>
  <app-platform-form
    [formData]="selectedPlatform"
    (onSubmitClbk)="onSubmitPlatformClbk()"
  >
  </app-platform-form>
</ng-template>

<ng-template #confirmDeleteTmpl>
  <div class="grid gap-4 p-4">
    <p class="text-sm text-gray-700 sm:text-base">
      Are you sure you want to delete platform
      <b>{{ selectedPlatform.platform }} ({{ selectedPlatform.domain }})</b>?
    </p>

    <div class="grid grid-cols-1 items-center justify-center gap-2 sm:grid-cols-2">
      <button
        type="button"
        class="base-btn rounded bg-red-600 px-4 py-2 text-white"
        (click)="deletePlatform()"
      >
        Confirm
      </button>

      <button
        type="button"
        class="base-btn rounded bg-gray-300 px-4 py-2 text-gray-800"
        (click)="dialog.closeAll()"
      >
        Cancel
      </button>
    </div>
  </div>
</ng-template>